import { useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { createContext, useCallback, useMemo, useState } from "react";
import { matchKeywordGroupsPackageReceipts } from "../graphql/queries/listKeywordGroups";

export const KeywordGroupBrandSelectionContext = createContext({});

export const KeywordGroupBrandSelectionProvider = ({ children }) => {
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [previousSelectedBrands, setPreviousSelectedBrands] = useState([]);

  const { data: keywordGroupsBrandsData, loading } = useQuery(
    matchKeywordGroupsPackageReceipts,
    {
      variables: {
        type: "BRAND",
        filters: {},
      },
    }
  );

  const notApplicableBrand = keywordGroupsBrandsData?.listKeywordGroups
    ?.filter((brand) => brand.name === "Not Applicable")
    .map((brand) => brand.id);

  const availableBrands = useMemo(() => {
    if (!keywordGroupsBrandsData) return [];
    const listAvailableIds = keywordGroupsBrandsData?.listKeywordGroups
      ?.filter(
        (product) =>
          !selectedBrands.includes(product.id) &&
          !notApplicableBrand.includes(product.id)
      )
      .map((product) => product.id);
    return listAvailableIds;
  }, [keywordGroupsBrandsData, notApplicableBrand, selectedBrands]);

  const toggleBrandItem = useCallback(
    (ids) => {
      if (typeof ids === "object" && !Array.isArray(ids)) return;

      const newBrandSet = ids;

      const itemsToDeselect = previousSelectedBrands.filter(
        (itemID) => !newBrandSet.includes(itemID)
      );

      setSelectedBrands((prevSelected) => {
        // Add new items if they are not present.
        const updatedSelection = newBrandSet.reduce(
          (acc, id) => {
            if (!acc.includes(id)) {
              acc.push(id);
            }
            return acc;
          },
          [...prevSelected]
        );
        // Remove unchecked items.
        return updatedSelection.filter((item) => !itemsToDeselect.includes(item));
      });
    },
    [previousSelectedBrands]
  );

  const handleRemovedBrandData = useCallback(
    (removedData) => {
      const newGloballySelected = selectedBrands.filter(
        (item) => !removedData.includes(item)
      );
      setSelectedBrands(newGloballySelected);
    },
    [selectedBrands]
  );

  const contextValues = useMemo(
    () => ({
      loading,
      keywordGroupsBrandsData: keywordGroupsBrandsData
        ? keywordGroupsBrandsData?.listKeywordGroups
        : [],
      selectedBrands,
      setSelectedBrands,
      availableBrands,
      previousSelectedBrands,
      setPreviousSelectedBrands,
      toggleBrandItem,
      handleRemovedBrandData,
    }),
    [
      loading,
      keywordGroupsBrandsData,
      selectedBrands,
      availableBrands,
      previousSelectedBrands,
      toggleBrandItem,
      handleRemovedBrandData,
    ]
  );

  return (
    <KeywordGroupBrandSelectionContext.Provider value={contextValues}>
      {children}
    </KeywordGroupBrandSelectionContext.Provider>
  );
};

KeywordGroupBrandSelectionProvider.propTypes = {
  children: PropTypes.node,
};
